import Component from 'vue-class-component';
import {
  _APP_STORE, 
  CLOSE_BOARD_SOCKET_DISCONNECTING_EVENT,
  CLOSE_SOCKET_DISCONNECTING_EVENT,
  DISCONNECTION_ALERT_TYPES,
  OBTAIN_TOKEN_EVENT,
  SHOW_SOCKET_DISCONNECTING_EVENT,
  SOCKET_ON_CONNECT_EVENT,
  STUDENT_SOCKET_CLASS_EVENTS,
  STUDENT_SOCKET_ROOM_EVENTS,
  TEACHER_SOCKET_TASK_EVENTS,
  DISCONNECTED_SOCKET_EVENT,
  RECONNECT_SOCKET_EVENT,
  FORWARD_SOCKET_MESSAGES_EVENT,
  SHOW_DISCONNECTED_MODAL_EVENT,
  APP_SOCKET_EVENTS,
  HIDE_DISCONNECTED_MODAL_EVENT} from '../values/app.values';
import { SERVER_HOSTNAME } from '@/shared/enums/request.enum';
import Vue from 'vue';
import KEYS from '@/values/keys';
import { MeetingsService } from '@/meetings/services/meetings.service';
import { MEETINGS_STORE } from '@/meetings/store/meetings.store';
import SocketEventManage from '../interfaces/socketEventManage.interface';
import WebSocketMessageResponse from '@/content/models/web-socket-message-response.model';
import SocketEmitEventManage from '../interfaces/socketEmitEventManage.interface';
import WebSocketMessageRequest from '@/content/models/web-socket-message-request.model';
import EdPresentationModeFactory from '@/content/factories/ed-presentation-mode.factory';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import EdDisconnectedModal from '@/components/common/ed-disconnected-modal/ed-disconnected-modal.component.vue';
import { AuthService } from '@/auth/services/auth.service';
import { ClassesService} from '@/classes/services/classes.service';
import { LOADING_EVENTS } from '../values/core.values';
import {HOMEWORKS_STORE} from '@/homework/store/homeworks.store';
import { AuthProvider } from '@/auth/providers/auth.provider';
import { HOMEWORK_SOCKET_EVENTS } from '@/homework/values/homeworks.values';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';

@Component({})
export default class SocketMixin extends Vue {

  private socketConnections : string[] = [];

  get isPreviousDisconnectionSocket(): boolean {
    return this.$store.state.AppStore.isPreviousDisconnectionSocket;
  }

  get isBoardPreviousDisconnectionSocket() : boolean {
    return this.$store.state.AppStore.isBoardPreviousDisconnectionSocket;
  }

  get isPublisher() : boolean {
    return this.$store.state.AuthStore.role === KEYS.ROLES.PUBLISHER;
  }

  get isPresentationMode() : boolean{
    return this.$store.state.CourseStore.isPresentationStudentMode || this.$store.state.CourseStore.isPresentationTeacherMode;
  }

  get isPresentationTeacherMode() : boolean{
    return this.$store.state.CourseStore.isPresentationTeacherMode;
  }

  get isPresentationModeGuestTeacher() : boolean {
    return this.$store.state.MeetingsStore.isPresentationModeGuestTeacher;
  }

  get isBoardActive() : boolean {
    return this.$store.state.HomeworksStore.isBoardActive;
  }

  get isEdupadActive() : boolean {
    return this.$store.state.HomeworksStore.isEdupadActive;
  }

  get isSubjectBuilderActive() : boolean {  
    return this.$store.state.AppStore.isSubjectBuilderActive;
  }

  get socketEventRoomForCurrentUser() {
    return HOMEWORK_SOCKET_EVENTS.response.replace('{username}', this.$store.state.AuthStore.user.userId);
  }

  async connectSocket(isReconnect: boolean = false) : Promise<void> {
    return new Promise((resolve, reject) => {
      const token = AuthProvider.getToken();
      const name = this.$Auth.Store.user.fullName; 

      if(!isReconnect){
        this.$EdSocket.connect(name, token, process.env[SERVER_HOSTNAME.SOCKET]).then(() => {
          this.$App.Store.setSocketConnected(true);
          this.$App.Store.setPresentationSocketConnected(true)
          this.validateObtainInitialValues();
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
      }
    });
  }

  private disconnectSocketManually() {
    this.$EdModal.closeModal();
  }

  private validateObtainInitialValues() {
    this.createListeners();
    this.$edEventBus.$emit(SOCKET_ON_CONNECT_EVENT);
  }

  private async createListeners(isReconnect : boolean = false) {
    try {
      const response = await MeetingsService.getUserClassRooms();
      const data = response;
      this.$store.commit(MEETINGS_STORE.MUTATIONS.SET_MEETINGS_CLASSES, data);
      if(!data || data.length === 0){
        return;
      }
      const classEvent = STUDENT_SOCKET_CLASS_EVENTS;
      const roomEvent = this.$Auth.Store.isPublisher ? [] : STUDENT_SOCKET_ROOM_EVENTS;
      data.forEach(classInfo => {
        this.createSpecificListener(classInfo.classId, classEvent, '{classId}', isReconnect);
        this.createSpecificListener(classInfo.meetingId, roomEvent, '{roomId}', isReconnect);
      });
      if (this.isPublisher) {
        await this.createListenersForPublishers(isReconnect);
      }
    } catch {
      this.$EdModal.createMessageModal({
        title: this.$t('common.error'),
        body:  this.$t('errors.communicationServer'),
        type:  KEYS.EVENT_TYPES.WARNING,
        action: () => window.location.reload()
      })
    }
  }

  private async createListenersForPublishers(isReconnect: boolean = false): Promise<void> {
    this.$store.commit(HOMEWORKS_STORE.MUTATIONS.RESET_ONLINE_HOMEWORK_DATA);

    this.$EdSocket.on(this.socketEventRoomForCurrentUser, (message : WebSocketMessageResponse) => {
      if( message.messageType == APP_SOCKET_EVENTS.MEDIA_ASSET_CONVERTED ) {
        this.$edEventBus.$emit(COURSES_EVENTS.ASSET_CONVERTED, JSON.parse(message.content));
      }
    });

    const data = await ClassesService.getAllClasses();
    if (!data || data.length == 0) {
      return;
    }
    data.forEach(c => this.createSpecificListener(c.id, TEACHER_SOCKET_TASK_EVENTS, '{classId}', isReconnect));
  }

  public createClassListener(classId: number) {
    this.createSpecificListener(classId, this.$Auth.Store.isPublisher ? [] : STUDENT_SOCKET_CLASS_EVENTS, '{classId}');
  }

  private createSpecificListener(topicId : number, lstEvents: SocketEventManage[], key : string, isReconnect : boolean = false) : void {
    lstEvents.forEach(eventDetail => {
      if(!isReconnect){
        let socketSubscription = eventDetail.socketEvent.replace(key, String(topicId));
        this.$EdSocket.on(socketSubscription, (message : WebSocketMessageResponse) => {
          this.$edEventBus.$emit(eventDetail.appEvent, { ...message, topicId });
        });
        this.socketConnections.push(socketSubscription);
      }
      this.emitEvents(topicId, key, eventDetail.socketEmitEvents);
    });
  }

  private emitEvents(topicId : number, key : string, socketEmitEvents : SocketEmitEventManage[]) : void{
    socketEmitEvents.forEach(eventEmit => {
      let message : WebSocketMessageRequest = EdPresentationModeFactory.createWebSocketMessageRequest(eventEmit.messageType, String(topicId));
      this.$EdSocket.emit(eventEmit.socketEmit.replace(key, String(topicId)), message);
    });
  }

  private showSocketDisconnecting({isBoard, typeMessage} : {isBoard: boolean, typeMessage : string}) : void {
    if(!this.isPreviousDisconnectionSocket && !this.isBoardPreviousDisconnectionSocket){
      if (typeMessage === DISCONNECTION_ALERT_TYPES.toast 
          || (!this.isPresentationModeGuestTeacher && !this.isPresentationTeacherMode && this.isBoardActive) 
          || this.isEdupadActive 
          || this.isSubjectBuilderActive) {
        this.$store.commit(MEETINGS_STORE.MUTATIONS.UPDATE_INITIAL_BOARDS_LOADED_EMITED, false);
        this.$EdToast.create({
          msg: `${this.$t('errors.offlineNetworkError')}`,
          type: TOAST_TYPES.WARNING,
          top: this.isBoardActive ? 'calc(100% - 35px)' : '2.8rem'
        });
      } else {
        this.$EdModal.createModal(EdDisconnectedModal, { isBoard });
      }

      if(isBoard){
        this.$App.Store.updateBoardPreviousDisconnectionSocket(true)
      }else {
        this.$App.Store.updatePreviousDisconnectionSocket(true)
        this.$store.commit(MEETINGS_STORE.MUTATIONS.CLEAR_PRESENTATION_MODE_MEETINGS);
        this.$edEventBus.$emit(DISCONNECTED_SOCKET_EVENT);
      }
    }
  }

  private showDisconnectedModal(isBoard : boolean) : void {
    this.$EdModal.createModal(EdDisconnectedModal, {isBoard});
  }

  private closeSocketDisconnecting(): void {
    if (this.isPreviousDisconnectionSocket) {
      if (!this.isPresentationMode && (this.isBoardActive || this.isEdupadActive || this.isSubjectBuilderActive)) {
        this.$EdToast.hide();
      }

      if(this.isPresentationMode || !(this.isBoardActive || this.isEdupadActive || this.isSubjectBuilderActive)){
        this.$EdModal.closeModal();
      }
      this.$store.commit(_APP_STORE.MUTATIONS.UPDATE_PREVIOUS_DISCONNECTION_SOCKET, false);
    }
  }

  private closeBoardSocketDisconnecting(messageType : string = '') : void {
    if(this.isBoardPreviousDisconnectionSocket){

      if (messageType === DISCONNECTION_ALERT_TYPES.toast) {
        this.$EdToast.hide();
      } else {
        this.$EdModal.closeModal();
      }
      this.$App.Store.updateBoardPreviousDisconnectionSocket(false)
    }
  }

  private disconectSocket() {
    let classId = this.$store.state.AppStore.userPosition.classId;
    // SocketIO Migration - Disable presence events
    // if(classId && this.isPublisher) {
    //   this.$EdSocket.emit(
    //     APP_SOCKET_EVENTS.TEACHER_VIEW_UPDATE_LOCATION
    //       .replace('{classId}', String(classId)), {
    //     username: this.$store.state.AuthStore.user.fullName,
    //     messageType: APP_SOCKET_MESSAGES_TYPE.LEAVE
    //   });
    // }
  }

  private async refreshToken(isReconnect: boolean = false) {
    try {
      await AuthService.getAccessToken();
      await this.connectSocket(isReconnect);
      this.$App.Store.setSocketConnected(true);
    } catch(err) {
      this.$App.Store.setSocketConnected(true);
    }
  }

  private async reconnectEmitSocketEvents() : Promise<void> {
    if(!this.isEdupadActive && !this.isBoardActive)
      this.$edEventBus.$emit(LOADING_EVENTS.SHOW);
    this.$App.Store.setPresentationSocketConnected(true)
    await this.createListeners(true);
    this.$edEventBus.$emit(FORWARD_SOCKET_MESSAGES_EVENT);
    this.$edEventBus.$emit(LOADING_EVENTS.HIDE);
  }

  async createSocketSession() {
    await this.connectSocket();
    this.$edEventBus.$on(OBTAIN_TOKEN_EVENT, this.refreshToken);
    this.$edEventBus.$on('createClassListener', (classId) => this.createClassListener(classId));
    this.$edEventBus.$on(SHOW_SOCKET_DISCONNECTING_EVENT, this.showSocketDisconnecting);
    this.$edEventBus.$on(CLOSE_SOCKET_DISCONNECTING_EVENT, this.closeSocketDisconnecting);
    this.$edEventBus.$on(CLOSE_BOARD_SOCKET_DISCONNECTING_EVENT, this.closeBoardSocketDisconnecting);
    this.$edEventBus.$on(SHOW_DISCONNECTED_MODAL_EVENT, this.showDisconnectedModal);
    this.$edEventBus.$on(HIDE_DISCONNECTED_MODAL_EVENT, this.disconnectSocketManually);
    window.addEventListener('beforeunload', this.disconectSocket);

    if(!this.$edEventBus['_events'][RECONNECT_SOCKET_EVENT]){
      this.$edEventBus.$on(RECONNECT_SOCKET_EVENT, this.reconnectEmitSocketEvents);
    }
  }

  beforeDestroy() {
    this.socketConnections.forEach(connection => this.$EdSocket.off(connection));
    this.socketConnections = [];
    this.$edEventBus.$off('createClassListener');
    this.$edEventBus.$off(SHOW_SOCKET_DISCONNECTING_EVENT);
    this.$edEventBus.$off(CLOSE_SOCKET_DISCONNECTING_EVENT);
    this.$edEventBus.$off(CLOSE_BOARD_SOCKET_DISCONNECTING_EVENT);
    this.$edEventBus.$off(OBTAIN_TOKEN_EVENT);
    this.$edEventBus.$off(RECONNECT_SOCKET_EVENT);
    this.$edEventBus.$off(SHOW_DISCONNECTED_MODAL_EVENT);
    this.$edEventBus.$off(HIDE_DISCONNECTED_MODAL_EVENT);
  }
  
}